/**
 * The club's API tokens — create, read the list, revoke.
 *
 * WHO SEES WHAT. A club admin sees every token the club has, including the
 * service tokens that outlive whoever made them. Anyone else sees only their
 * own personal tokens: a viewer may hold a PAT (Ben, 2026-08-29) but has no
 * business reading the club's integration credentials. The server enforces
 * that in /settings/api-tokens; this only decides what is drawn.
 *
 * THE PLAINTEXT APPEARS ONCE. It is in the create response and nowhere else,
 * because only a hash is stored. The panel says so before you close it.
 */
import { useCallback, useEffect, useState } from 'react'

import { trackEvent } from '../lib/analytics'
import { api } from '../lib/api'
import './admin.css'
import './api-tokens.css'

type Token = {
  id: number
  name: string
  kind: 'personal' | 'service'
  prefix: string
  scopes: string[]
  status: 'active' | 'expired' | 'revoked'
  owner: string | null
  created_at: string | null
  expires_at: string | null
  /** A service key issued before 2026-10-21 may carry no expiry. */
  never_expires?: boolean
  last_used_at: string | null
}

type Level = 'read' | 'read_write'

type Listing = {
  tokens: Token[]
  may_manage: boolean
  issuable_scopes: { personal: string[]; service: string[] }
  /** Exactly what each access level grants this issuer, per kind — the
   *  server's definition, so the form never guesses what "read" means. */
  level_scopes?: Record<'personal' | 'service', Record<Level, string[]>>
  default_days: number
  max_days?: number
}

// The server's cap (MAX_LIFETIME); the listing says it, this is the fallback.
const MAX_DAYS = 730

function when(iso: string | null): string {
  if (!iso) return '—'
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function ApiTokens() {
  const [data, setData] = useState<Listing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [minted, setMinted] = useState<string | null>(null)

  // create form
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'personal' | 'service'>('personal')
  const [scopes, setScopes] = useState<string[]>([])
  // The level a club actually picks. `custom` keeps the checkbox list, which
  // is what staff and anyone with an unusual integration still need.
  const [access, setAccess] = useState<Level | 'custom'>('read')
  const [days, setDays] = useState(90)
  const [reason, setReason] = useState('')

  const load = useCallback(async () => {
    try {
      setData(await api<Listing>('/api/v1/settings/api-tokens'))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The tokens could not be read.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const create = async () => {
    setError(null)
    try {
      const res = await api<{ token: Token & { plaintext: string } }>(
        '/api/v1/settings/api-tokens',
        { method: 'POST', body: JSON.stringify({
          name, kind, reason,
          // A level OR explicit scopes, never both — the server prefers
          // scopes when given, and sending both would hide which one applied.
          ...(access === 'custom' ? { scopes } : { access }),
          // Every key expires; a long-lived one takes the maximum.
          expires_in_days: days,
        }) })
      setMinted(res.token.plaintext)
      // The old API-keys page fired this on its create form; the scoped
      // tokens page is its successor, so the event keeps the name (and
      // key_name property) the Mixpanel trend has carried since the
      // Jinja app. kind/access are new, additive properties.
      trackEvent('API Key Created', {
        key_name: name || 'unnamed', kind,
        access: access === 'custom' ? scopes.join(' ') : access,
      })
      setCreating(false)
      setName(''); setScopes([]); setReason('')
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The token could not be created.')
    }
  }

  const revoke = async (token: Token) => {
    // Revoking is not undoable and breaks whatever is using it, so it asks.
    const why = window.prompt(
      `Revoke “${token.name}”? Anything using it stops working immediately.\n\n`
      + 'Reason (recorded in the audit trail):')
    if (!why) return
    try {
      await api(`/api/v1/settings/api-tokens/${token.id}/revoke`,
                { method: 'POST', body: JSON.stringify({ reason: why }) })
      // Same naming as the restored 'API Key Created' (the old app's names).
      trackEvent('API Key Revoked', { key_name: token.name, kind: token.kind })
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The token could not be revoked.')
    }
  }

  const available = data ? data.issuable_scopes[kind] : []
  const maxDays = data?.max_days ?? MAX_DAYS
  // In a level, the scope list is a preview of what that level grants:
  // everything else (for Read, every write scope) is greyed out.
  const levelGrants = access === 'custom' ? null
    : new Set(data?.level_scopes?.[kind]?.[access] ?? [])

  return (
    <>
      <h2 className="st-h2">Your keys</h2>
      <p className="page-sub">
        Tokens let scripts and integrations read this club&rsquo;s data over the
        API. Every token expires — two years at most — and can be revoked at
        any time. A <strong>personal</strong> token acts as you and can never do
        more than you can; a <strong>service</strong> token belongs to the club
        and keeps working when people change.
      </p>

      {error && <p className="error">{error}</p>}

      {minted && (
        <div className="panel tok-minted">
          <h2 className="tok-minted__title">Copy this now</h2>
          <p className="page-sub">
            This is the only time the token is shown — it is stored hashed, so
            it cannot be shown again. If you lose it, revoke it and make
            another.
          </p>
          <div className="tok-secret">
            <code>{minted}</code>
            <button className="adm-btn"
                    onClick={() => { void navigator.clipboard?.writeText(minted) }}>
              Copy
            </button>
          </div>
          <button className="adm-btn adm-btn--quiet" onClick={() => setMinted(null)}>
            Done
          </button>
        </div>
      )}

      <div className="adm-bar">
        {!creating && (
          <button className="adm-btn" onClick={() => setCreating(true)}>
            New token
          </button>
        )}
        <span className="adm-bar__spacer" />
        <span className="t-data" style={{ color: 'var(--color-text-faint)' }}>
          {data ? `${data.tokens.length} token${data.tokens.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {creating && (
        <div className="panel tok-form">
          <h2 className="tok-form__title">New token</h2>

          <div className="tok-grid">
            <label className="adm-field">
              <span className="t-label">Name</span>
              <input className="adm-input" value={name} autoFocus
                     onChange={(e) => setName(e.target.value)}
                     placeholder="season reporting script" />
            </label>
            <label className="adm-field">
              <span className="t-label">Kind</span>
              <select className="adm-select" value={kind} onChange={(e) => {
                setKind(e.target.value as 'personal' | 'service')
                setScopes([])
              }}>
                <option value="personal">Personal — acts as me</option>
                {data?.may_manage && (
                  <option value="service">Service — belongs to the club</option>
                )}
              </select>
            </label>
            <label className="adm-field">
              <span className="t-label">Access</span>
              <select className="adm-select" value={access}
                      onChange={(e) => setAccess(e.target.value as typeof access)}>
                <option value="read">Read — fetch matches, events and analytics</option>
                <option value="read_write">Read and write — also upload and label</option>
                <option value="custom">Custom — pick individual scopes</option>
              </select>
            </label>
            <label className="adm-field">
              <span className="t-label">Expires in</span>
              <div className="tok-days">
                <input className="adm-input" type="number" min={1} max={maxDays}
                       value={days}
                       onChange={(e) => setDays(Number(e.target.value))} />
                <span className="tok-days__unit">days · {maxDays} max</span>
              </div>
              <span className="tok-hint">
                Need a long-lived key? Set it to {maxDays} days (2 years), the
                longest a key can last.
              </span>
            </label>
          </div>

          {access !== 'custom' && (
            <p className="muted tok-levelnote">
              {access === 'read'
                ? 'Read access: this key can fetch matches, events and analytics, and change nothing.'
                : 'Read and write: this key can also upload footage and write labels.'}
              {' '}It can never do more than you can in this club.
            </p>
          )}

          <div className="adm-field">
            <span className="t-label">
              {levelGrants ? 'What this level grants' : 'What it may do'}
            </span>
            <div className="tok-scopes">
              {available.length === 0 && (
                <span className="muted">Nothing available to grant.</span>
              )}
              {available.map((scope) => {
                // A level fixes the scopes; pick Custom to choose them.
                const granted = levelGrants ? levelGrants.has(scope) : scopes.includes(scope)
                const off = levelGrants !== null && !granted
                return (
                  <label key={scope}
                         className={`tok-scope${granted ? ' is-on' : ''}${off ? ' is-unavailable' : ''}`}
                         title={off ? (access === 'read'
                           ? 'Not granted by Read — pick Read and write or Custom'
                           : 'Not granted by this level — pick Custom') : undefined}>
                    <input type="checkbox" checked={granted}
                           disabled={levelGrants !== null}
                           onChange={(e) => setScopes(e.target.checked
                             ? [...scopes, scope]
                             : scopes.filter((s) => s !== scope))} />
                    <code>{scope}</code>
                  </label>
                )
              })}
            </div>
          </div>

          <label className="adm-field">
            <span className="t-label">Reason</span>
            <input className="adm-input" value={reason}
                   onChange={(e) => setReason(e.target.value)}
                   placeholder="why this token exists — recorded in the audit trail" />
          </label>

          <div className="tok-actions">
            <button className="adm-btn" onClick={() => { void create() }}
                    disabled={!name.trim() || !reason.trim()
                              || (access === 'custom' && scopes.length === 0)}>
              Create token
            </button>
            <button className="adm-btn adm-btn--quiet" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <table className="adm-table tok-table">
        <thead>
          <tr>
            <th>Name</th><th>Kind</th><th>Prefix</th><th>Scopes</th>
            <th>Owner</th><th>Expires</th><th>Last used</th><th>Status</th><th />
          </tr>
        </thead>
        <tbody>
          {data === null && (
            <tr><td colSpan={9} className="adm-empty">Loading…</td></tr>
          )}
          {data?.tokens.length === 0 && (
            <tr><td colSpan={9} className="adm-empty">
              No tokens yet. Anything that needs one will say so.
            </td></tr>
          )}
          {data?.tokens.map((t) => (
            <tr key={t.id}>
              <td className="adm-primary">{t.name}</td>
              <td><span className={`adm-chip adm-chip--${t.kind}`}>{t.kind}</span></td>
              <td><code className="tok-prefix">{t.prefix}…</code></td>
              <td>
                <div className="adm-chips">
                  {t.scopes.map((s) => <span key={s} className="adm-chip">{s}</span>)}
                </div>
              </td>
              <td>{t.owner ?? <span className="adm-chip__role">the club</span>}</td>
              <td className="adm-when">
                {t.never_expires ? <span className="adm-chip__role">never</span>
                  : when(t.expires_at)}
              </td>
              <td className="adm-when">
                {t.last_used_at ? when(t.last_used_at)
                  : <span className="adm-chip__role">never</span>}
              </td>
              <td>
                <span className={`adm-chip tok-status tok-status--${t.status}`}>
                  {t.status}
                </span>
              </td>
              <td>
                {t.status === 'active' && (
                  <button className="adm-btn adm-btn--danger"
                          onClick={() => { void revoke(t) }}>Revoke</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export default ApiTokens
